import {
  ExportApiId,
  ExportType,
  ExportV1,
  STATUS_LABEL,
  SUB_TYPE_LABEL,
  TYPE_LABEL,
} from '@/components/Studies/StudyExports/ExportsList/Exports.model';
import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import { Dropdown, DropdownMenuItem } from '@/components/UI/Dropdown';
import type { FilterOption, TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import UserAvatar from '@/components/UI/UserAvatar/UserAvatar';
import { FilterTypes } from '@/constants/FilterTypes';
import type { components } from '@/generated/internal-api/openapi-types';
import { _isEmpty, _notNil } from '@/littledash';
import type { BaseUser } from '@/model/User.model';
import { RiMoreFill } from 'react-icons/ri';

type ExportStatus = components['schemas']['ExportStatus.schema'];

const statusOptions = (Object.keys(STATUS_LABEL) as Array<ExportStatus>).map((value) => ({
  name: STATUS_LABEL[value],
  value,
}));

const typeOptions = (Object.keys(TYPE_LABEL) as Array<ExportType>).map((value) => ({
  name: TYPE_LABEL[value],
  value,
}));

/**
 * Builds the filter options list for the Exports table — status, type, and user.
 * Mirrors the pattern used by Samples so the filter UX is consistent across list views.
 */
export const generateFilterOptions = (users: Array<BaseUser>): Array<FilterOption> => {
  const userOptions = users.map(({ name, api_id }) => ({ name, value: api_id }));

  return [
    {
      value: 'status',
      name: 'Status',
      operations: [
        { name: 'is equal to', value: FilterTypes.eq, options: statusOptions },
        { name: 'is not equal to', value: FilterTypes.ne, options: statusOptions },
      ],
    },
    {
      value: 'type',
      name: 'Export Type',
      operations: [
        { name: 'is equal to', value: FilterTypes.eq, options: typeOptions },
        { name: 'is not equal to', value: FilterTypes.ne, options: typeOptions },
      ],
    },
    {
      value: 'user',
      name: 'User',
      operations: [
        { name: 'is equal to', value: FilterTypes.eq, options: userOptions },
        { name: 'is not equal to', value: FilterTypes.ne, options: userOptions },
      ],
    },
  ];
};

interface ColumnHandlers {
  isTeamOwner: boolean;
  onDownload: (exportApiId: ExportApiId, fileName: string | null) => void;
  onRename: (row: ExportV1, refetch: () => Promise<void>) => void;
}

/**
 * Builds the column definitions for the Exports table. Kept here so the component
 * file stays focused on orchestration (data loading + handlers) rather than rendering.
 */
export const buildColumns = ({
  isTeamOwner,
  onDownload,
  onRename,
}: ColumnHandlers): Array<TanStackColumnDef<ExportV1>> => [
  {
    id: 'status',
    header: 'Status',
    accessorKey: 'status',
    size: 100,
    enableSorting: false,
    isVisible: true,
    cell: ({ row: { original } }: { row: { original: ExportV1 } }) => STATUS_LABEL[original.status],
  },
  {
    id: 'file_name',
    header: 'File name',
    accessorKey: 'file_name',
    size: 300,
    enableSorting: true,
    isVisible: true,
    cell: ({ row: { original } }: { row: { original: ExportV1 } }) => {
      if (!_notNil(original.file_name)) {
        return '—';
      }
      if (original.status === 'completed') {
        return (
          <button
            type="button"
            className="link-button"
            onClick={() => onDownload(original.api_id, original.file_name)}
            data-testid={`exports-list__file-name--${original.api_id}`}
          >
            {original.file_name}
          </button>
        );
      }
      return original.file_name;
    },
  },
  {
    id: 'type',
    header: 'Export Type',
    accessorKey: 'type',
    size: 120,
    enableSorting: false,
    isVisible: true,
    cell: ({ row: { original } }: { row: { original: ExportV1 } }) => TYPE_LABEL[original.type],
  },
  {
    id: 'sub_types',
    header: 'Study report contents',
    accessorKey: 'sub_types',
    size: 200,
    enableSorting: false,
    isVisible: true,
    cell: ({ row: { original } }: { row: { original: ExportV1 } }) => {
      if (_isEmpty(original.sub_types)) {
        return '-';
      }
      return original.sub_types?.map((subType) => SUB_TYPE_LABEL[subType]).join(', ');
    },
  },
  {
    id: 'created_at',
    header: 'Created At',
    accessorKey: 'created_at',
    size: 100,
    enableSorting: true,
    isVisible: true,
    cell: ({ row: { original } }: { row: { original: ExportV1 } }) => <DateTimeRenderer value={original.created_at} />,
  },
  {
    id: 'created_by',
    header: 'Created by',
    accessorKey: 'created_by',
    size: 20,
    enableSorting: false,
    isVisible: isTeamOwner,
    cell: ({ row: { original } }: { row: { original: ExportV1 } }) => (
      <UserAvatar
        user={original.created_by ?? undefined}
        className="ml1 dib"
        tooltip={original.created_by?.name ?? undefined}
      />
    ),
  },
  {
    id: 'actions',
    isVisible: true,
    size: 35,
    enableSorting: false,
    cell: ({
      row: { original },
      table,
    }: {
      row: { original: ExportV1 };
      table: { options: { meta?: { apiTable?: { fetchTableData: () => Promise<void> } } } };
    }) => {
      const refetch = table?.options?.meta?.apiTable?.fetchTableData ?? (async () => {});
      const canRename = original.status === 'completed' && _notNil(original.file_name);
      if (!canRename) {
        return null;
      }
      return (
        <Dropdown
          renderMenu={() => (
            <DropdownMenuItem
              testId={`exports-list__rename--${original.api_id}`}
              onClick={() => onRename(original, refetch)}
            >
              Rename
            </DropdownMenuItem>
          )}
        >
          <RiMoreFill size={22} />
        </Dropdown>
      );
    },
  },
];
